import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Loader2, Mail, MapPin, Send } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORES = [
  "Gobierno / CNE",
  "Infraestructura & Redes",
  "Software a medida",
  "Ciberseguridad",
  "Otro",
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 font-body text-sm text-white placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-neon/70 focus:bg-white/[0.07] focus:ring-2 focus:ring-neon/20";

const Contact = () => {
  const [form, setForm] = useState({ nombre: "", empresa: "", email: "", sector: SECTORES[0], mensaje: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data.ok) {
        setSent(true);
        toast.success("Mensaje enviado. Nuestro equipo le contactará en breve.");
        setForm({ nombre: "", empresa: "", email: "", sector: SECTORES[0], mensaje: "" });
      } else {
        throw new Error("unexpected response");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail;
      toast.error(
        typeof msg === "string"
          ? `No se pudo enviar: ${msg}`
          : "No se pudo enviar el mensaje. Revise los datos e intente de nuevo."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="scroll-mt-24 bg-obsidian py-24 sm:py-28" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
        <Reveal>
          <Eyebrow>04 — Contacto</Eyebrow>
          <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[2.9rem] lg:leading-[1.1]">
            Hablemos de su próximo proyecto crítico<span className="text-neon">.</span>
          </h2>
          <p className="mt-5 max-w-md font-body text-[15px] leading-relaxed text-slate-400 sm:text-base">
            Cuéntenos su requerimiento técnico o de cumplimiento y un ingeniero de nuestro equipo
            le responderá con una propuesta clara.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex items-center gap-4" data-testid="contact-info-email">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-neon">
                <Mail className="h-4.5 w-4.5" strokeWidth={1.6} />
              </span>
              <span className="font-body text-sm text-slate-300">contacto@tioccidente.com</span>
            </li>
            <li className="flex items-center gap-4" data-testid="contact-info-location">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-neon">
                <MapPin className="h-4.5 w-4.5" strokeWidth={1.6} />
              </span>
              <span className="font-body text-sm text-slate-300">
                Occidente de México — Jalisco · Michoacán · Nayarit · Colima
              </span>
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.12}>
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="rounded-[1.6rem] border border-white/10 bg-ink p-7 sm:p-9"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label htmlFor="nombre" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Nombre completo
                </label>
                <input
                  id="nombre"
                  data-testid="contact-input-nombre"
                  className={inputCls}
                  placeholder="Ej. Ing. Carlos Mendoza"
                  value={form.nombre}
                  onChange={set("nombre")}
                  required
                  minLength={2}
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="empresa" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Organización / Institución
                </label>
                <input
                  id="empresa"
                  data-testid="contact-input-empresa"
                  className={inputCls}
                  placeholder="Ej. Ministerio / Empresa privada"
                  value={form.empresa}
                  onChange={set("empresa")}
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="email" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Correo corporativo
                </label>
                <input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  className={inputCls}
                  placeholder="carlos@empresa.gob.ve"
                  value={form.email}
                  onChange={set("email")}
                  required
                />
              </div>
              <div className="relative sm:col-span-1">
                <label htmlFor="sector" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Sector
                </label>
                <select
                  id="sector"
                  data-testid="contact-input-sector"
                  className={`${inputCls} appearance-none pr-10`}
                  value={form.sector}
                  onChange={set("sector")}
                >
                  {SECTORES.map((s) => (
                    <option key={s} value={s} className="bg-ink text-white">
                      {s}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute bottom-4 right-3.5 h-4 w-4 text-slate-500" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="mensaje" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Requerimiento técnico
                </label>
                <textarea
                  id="mensaje"
                  data-testid="contact-input-mensaje"
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder="Describa su proyecto o necesidad de cumplimiento..."
                  value={form.mensaje}
                  onChange={set("mensaje")}
                  required
                  minLength={5}
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="group mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-neon py-4 font-body text-sm font-bold text-obsidian transition-all duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
                </>
              ) : (
                <>
                  Enviar requerimiento
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
            {sent && (
              <p data-testid="contact-success-message" className="mt-4 text-center font-body text-sm text-neon">
                Mensaje recibido correctamente. ¡Gracias por confiar en TIO!
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
